import { useCallback, useState } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import SearchParams from "simplydo/core/searchParams";
import {
  Button,
  Divider,
  Header,
  Icon,
  Loader,
  Message,
} from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";
import constants from "utils/constants";

import AppStyledDashboard from "components/AppStyledDashboard";
import PageLoadError from "components/lib/PageLoadError";

export const UnsubscribeNotifications = () => {
  const user = useAppSelector((state) => state.user);

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [didUnsubscribe, setDidUnsubscribe] = useState(false);
  const [hasClickedUnsubscribe, setHasClickedUnsubscribe] = useState(false);
  const [error, setError] = useState("");

  const searchParams = new SearchParams(window.location.search);
  const userId = searchParams.has("user")
    ? searchParams.get("user")
    : user?._id;
  const subscription = searchParams.get("subscription");
  const token = searchParams.get("token");

  const { t } = useTranslation();
  const eventInfo = t(
    constants.subscriptions.find((s) => s.key === subscription)?.text || "",
  );

  const unsubscribeFromNotification = useCallback(() => {
    if (!userId || !subscription || !token) {
      return;
    }

    setLoading(true);
    setError("");
    api.users.unsubscribeFromNotification(
      userId,
      subscription,
      token,
      ({ didUnsubscribe: serverDidUnsubscribe }) => {
        setHasClickedUnsubscribe(true);
        setSuccess(true);
        setLoading(false);
        setDidUnsubscribe(serverDidUnsubscribe);
      },
      (err) => {
        setLoading(false);
        setHasClickedUnsubscribe(true);
        setError(err.message);
      },
    );
  }, [userId, subscription, token]);

  if (!eventInfo) {
    return (
      <AppStyledDashboard
        excludeWelcomeText
        content={
          <div>
            <Message negative>
              We could not unsubscribe you from this type of email. Please
              contact an administrator.
            </Message>
            {!user ? (
              <>
                <Divider />
                <p>
                  You can still log in to view your account or sign up below.
                </p>
                <Button
                  primary
                  as={Link}
                  to={`/login`}
                  style={{ marginBottom: 5 }}
                >
                  {t("entry.login.loginOrSignUp")}
                </Button>
              </>
            ) : null}
          </div>
        }
      />
    );
  }

  if (!userId && !user?._id) {
    return (
      <PageLoadError title={t("notifications.unsubscribe.requiresLogin")} />
    );
  }

  return (
    <AppStyledDashboard
      excludeWelcomeText
      padded="very"
      content={
        <div>
          {!hasClickedUnsubscribe ? (
            <div>
              <Header>{t("notifications.unsubscribe.title")}</Header>
              <p>
                {t("notifications.unsubscribe.description")}{" "}
                <b>&quot;{eventInfo}&quot;</b>
              </p>

              <p>{t("notifications.unsubscribe.confirm")}</p>
              <Button onClick={() => unsubscribeFromNotification()}>
                {t("notifications.unsubscribe.action")}
              </Button>
            </div>
          ) : (
            <div>
              {loading ? (
                <Loader />
              ) : (
                <div>
                  {success ? (
                    didUnsubscribe ? (
                      <Message success>
                        <Icon name="check" />
                        {t("notifications.unsubscribe.success")}{" "}
                        <b>&quot;{eventInfo}&quot;</b>
                      </Message>
                    ) : (
                      <Message>
                        <Icon name="exclamation" />
                        {t(
                          "notifications.unsubscribe.alreadyUnsubscribed",
                        )}{" "}
                        <b>&quot;{eventInfo}&quot;</b>
                      </Message>
                    )
                  ) : (
                    <Message negative>{error}</Message>
                  )}

                  <div>
                    {user ? (
                      <Button basic as={Link} to="/preferences/notifications">
                        Review all notification preferences
                      </Button>
                    ) : null}
                    <Button primary as={Link} to="/">
                      {t("generic.done")}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      }
    />
  );
};

export default UnsubscribeNotifications;
